import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import {
  Globe,
  Shield,
  AlertTriangle,
  Plug,
  Users,
  Heart,
  DollarSign,
  Scale,
  FileText,
  Map,
  BookOpen,
  Building2,
  Mail,
  Briefcase,
} from "lucide-react";
import renestLogo from "@/assets/renest-logo-transparent.png";
import LanguageSelector from "./LanguageSelector";
import MobileNav from "./MobileNav";
import { cn } from "@/lib/utils";
import React from "react";

const ListItem = React.forwardRef<
  React.ElementRef<"a">,
  React.ComponentPropsWithoutRef<"a"> & { icon?: React.ReactNode; title: string; description: string }
>(({ className, title, description, icon, ...props }, ref) => {
  return (
    <li>
      <NavigationMenuLink asChild>
        <a
          ref={ref}
          className={cn(
            "block select-none rounded-lg p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
            className
          )}
          {...props}
        >
          <div className="flex items-start gap-3">
            {icon && (
              <div className="mt-0.5 text-muted-foreground">
                {icon}
              </div>
            )}
            <div>
              <div className="text-sm font-medium leading-none mb-1.5">{title}</div>
              <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                {description}
              </p>
            </div>
          </div>
        </a>
      </NavigationMenuLink>
    </li>
  );
});
ListItem.displayName = "ListItem";

const MarketingHeader = () => {
  const { t } = useTranslation();

  const productItems = [
    {
      icon: <Globe className="h-5 w-5" />,
      title: t('marketingHeader.menu.workationManagement.title'),
      description: t('marketingHeader.menu.workationManagement.description'),
      href: "#workation-management",
    },
    {
      icon: <Shield className="h-5 w-5" />,
      title: t('marketingHeader.menu.complianceRisk.title'),
      description: t('marketingHeader.menu.complianceRisk.description'),
      href: "#compliance-risk",
    },
    {
      icon: <AlertTriangle className="h-5 w-5" />,
      title: t('marketingHeader.menu.dutyCare.title'),
      description: t('marketingHeader.menu.dutyCare.description'),
      href: "#duty-of-care",
    },
    {
      icon: <Plug className="h-5 w-5" />,
      title: t('marketingHeader.menu.integrations.title'),
      description: t('marketingHeader.menu.integrations.description'),
      href: "#integrations",
    },
  ];

  const solutionsItems = [
    {
      icon: <Users className="h-5 w-5" />,
      title: t('marketingHeader.menu.hrTeams.title'),
      description: t('marketingHeader.menu.hrTeams.description'),
      href: "#hr-teams",
    },
    {
      icon: <Heart className="h-5 w-5" />,
      title: t('marketingHeader.menu.peopleTalent.title'),
      description: t('marketingHeader.menu.peopleTalent.description'),
      href: "#people-talent",
    },
    {
      icon: <DollarSign className="h-5 w-5" />,
      title: t('marketingHeader.menu.finance.title'),
      description: t('marketingHeader.menu.finance.description'),
      href: "#finance",
    },
    {
      icon: <Scale className="h-5 w-5" />,
      title: t('marketingHeader.menu.legal.title'),
      description: t('marketingHeader.menu.legal.description'),
      href: "#legal",
    },
  ];

  const resourcesItems = [
    {
      icon: <FileText className="h-5 w-5" />,
      title: t('marketingHeader.menu.policyTemplates.title'),
      description: t('marketingHeader.menu.policyTemplates.description'),
      href: "#policy-templates",
    },
    {
      icon: <Map className="h-5 w-5" />,
      title: t('marketingHeader.menu.countryGuides.title'),
      description: t('marketingHeader.menu.countryGuides.description'),
      href: "/country-guides",
    },
    {
      icon: <BookOpen className="h-5 w-5" />,
      title: t('marketingHeader.menu.blog.title'),
      description: t('marketingHeader.menu.blog.description'),
      href: "#blog",
    },
  ];

  const companyItems = [
    {
      icon: <Building2 className="h-5 w-5" />,
      title: t('marketingHeader.menu.about.title'),
      description: t('marketingHeader.menu.about.description'),
      href: "/about",
    },
    {
      icon: <Mail className="h-5 w-5" />,
      title: t('marketingHeader.menu.contact.title'),
      description: t('marketingHeader.menu.contact.description'),
      href: "/contact",
    },
    {
      icon: <Briefcase className="h-5 w-5" />,
      title: t('marketingHeader.menu.careers.title'),
      description: t('marketingHeader.menu.careers.description'),
      href: "#careers",
    },
  ];

  const scrollToDemo = () => {
    const demoSection = document.getElementById('final-cta');
    if (demoSection) {
      demoSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between px-4">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <img
            src={renestLogo}
            alt="Renest Logo"
            className="h-12 sm:h-14 w-auto object-contain"
            width="192"
            height="192"
          />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-1">
          <NavigationMenu>
            <NavigationMenuList>
              {/* Product */}
              <NavigationMenuItem>
                <NavigationMenuTrigger className="bg-transparent">
                  {t('marketingHeader.product')}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[500px] gap-1 p-4 md:grid-cols-2">
                    {productItems.map((item) => (
                      <ListItem
                        key={item.title}
                        title={item.title}
                        description={item.description}
                        icon={item.icon}
                        href={item.href}
                      />
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>

              {/* Solutions */}
              <NavigationMenuItem>
                <NavigationMenuTrigger className="bg-transparent">
                  {t('marketingHeader.solutions')}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[500px] gap-1 p-4 md:grid-cols-2">
                    {solutionsItems.map((item) => (
                      <ListItem
                        key={item.title}
                        title={item.title}
                        description={item.description}
                        icon={item.icon}
                        href={item.href}
                      />
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>

              {/* Resources */}
              <NavigationMenuItem>
                <NavigationMenuTrigger className="bg-transparent">
                  {t('marketingHeader.resources')}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[400px] gap-1 p-4">
                    {resourcesItems.map((item) => (
                      <ListItem
                        key={item.title}
                        title={item.title}
                        description={item.description}
                        icon={item.icon}
                        href={item.href}
                      />
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>

              {/* Pricing */}
              <NavigationMenuItem>
                <NavigationMenuLink
                  href="/pricing"
                  className="group inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none"
                >
                  {t('marketingHeader.pricing')}
                </NavigationMenuLink>
              </NavigationMenuItem>

              {/* Company */}
              <NavigationMenuItem>
                <NavigationMenuTrigger className="bg-transparent">
                  {t('marketingHeader.company')}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[350px] gap-1 p-4">
                    {companyItems.map((item) => (
                      <ListItem
                        key={item.title}
                        title={item.title}
                        description={item.description}
                        icon={item.icon}
                        href={item.href}
                      />
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
        </div>

        {/* Desktop CTA Buttons */}
        <div className="hidden lg:flex items-center gap-3">
          <LanguageSelector />
          <Button variant="ghost" asChild>
            <Link to="/auth">{t('marketingHeader.login')}</Link>
          </Button>
          <Button
            onClick={scrollToDemo}
            className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-6"
          >
            {t('marketingHeader.bookDemo')}
          </Button>
        </div>

        {/* Mobile Navigation */}
        <div className="lg:hidden">
          <MobileNav
            productItems={productItems}
            solutionsItems={solutionsItems}
            resourcesItems={resourcesItems}
            companyItems={companyItems}
            onBookDemo={scrollToDemo}
          />
        </div>
      </div>
    </header>
  );
};

export default MarketingHeader;
